import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  SortingState, 
  ColumnFiltersState,
} from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore } from "@/app/store/authStore";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsArrayOf, createParser } from 'nuqs';
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { Party, PartyCreate, PartyUpdate, PartyGroup } from "@/app/types/party";
import { Site } from "@/app/types/site";
import { 
  listParties, 
  createParty, 
  updateParty, 
  deleteParty,
  listPartyGroups, 
} from "@/app/services/partyService";
import { listSites } from "@/app/services/siteService";
import { usePreferenceStore } from "@/app/store/preferenceStore";
import { columns as baseColumns } from "./components/columns";
import PartyForm from "./components/PartyForm";
import PartyDataTable from "./components/PartyDataTable";
import PartyGroupManagement from "./components/PartyGroupManagement";
import ClientUserDialog from "./components/ClientUserDialog";

// Define selectors for auth store
const selectUser = (state: { user: any }): any => state.user;
const selectLogoutAction = (state: { logoutAction: () => void }): () => void => state.logoutAction;
const selectIsLoading = (state: { isLoading: boolean }): boolean => state.isLoading;

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return []
    const [field, direction] = value.split('-')
    return [{
      id: field,
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return ''
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

const PartyPage = () => {
  const queryClient = useQueryClient();
  
  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isClientUserDialogOpen, setIsClientUserDialogOpen] = useState(false);
  const [clientUserMode, setClientUserMode] = useState<'create' | 'reset'>('create');
  const [currentParty, setCurrentParty] = useState<Party | null>(null);
  
  // Use React Router's useSearchParams for the group param (set externally via sidebar navigation)
  const [searchParams] = useSearchParams();
  const group = searchParams.get('group');

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([]))

  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    name: parseAsString,
    phone: parseAsString,
    email: parseAsString,
    gstin: parseAsString,
    gst_type: parseAsString,
    party_group_id: parseAsString,
    site_id: parseAsString,
    state: parseAsString,
    source: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })
  
  // Build columnFilters from nuqs state for compatibility with existing code
  const columnFilters = useMemo<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];
    Object.entries(filterStates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        filters.push({ id: key, value });
      }
    });
    return filters;
  }, [filterStates]);
  
  const [pageSize] = useState(10);
  const pagination = {
    pageIndex: page,
    pageSize,
    pageCount: 0
  };
  
  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Get organization ID from user data
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  
  // Get user's default site preference
  const { defaultSiteId } = usePreferenceStore();

  // Query to fetch party groups (before queryParams so we can resolve group name → ID)
  const {
    data: groupsResponse,
    isLoading: isGroupsLoading,
  } = useQuery<PaginatedResponse<PartyGroup>>({
    queryKey: ['partyGroups', userOrgId],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      const params: ListQueryParams = {
        skip: 0,
        limit: 100,
        sort_by: 'name',
        sort_dir: 'asc'
      };
      return listPartyGroups(params, userOrgId);
    },
    enabled: !!userOrgId,
  });

  // Query to fetch sites
  const {
    data: sitesResponse,
    isLoading: isSitesLoading,
  } = useQuery<PaginatedResponse<Site>>({
    queryKey: ['sites', userOrgId],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      const params: ListQueryParams = {
        skip: 0,
        limit: 100,
        sort_by: 'name',
        sort_dir: 'asc',
        show_all: true // Get all sites for the organization
      };
      return listSites(params, userOrgId);
    },
    enabled: !!userOrgId,
  });

  // Parse party groups and sites for forms
  const partyGroups = groupsResponse?.items || [];
  const sites = sitesResponse?.items || [];

  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: page * pageSize,
      limit: pageSize,
    };

    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }

    // Build filter list
    const filtersList: any[] = [];

    // Add column filters (skip party_group_id if group param overrides it)
    columnFilters.forEach((filter) => {
      if (filter.value !== undefined && filter.value !== null && filter.value !== '') {
        if (filter.id === 'party_group_id' && group) return; // Group param takes precedence
        const textFields = ['name', 'phone', 'email', 'gstin'];
        const operator = textFields.includes(filter.id) ? 'icontains' : 'eq';
        filtersList.push({
          field: filter.id,
          op: operator,
          value: filter.value
        });
      }
    });

    // Add group pre-filter from URL param (resolve name → ID)
    if (group && partyGroups.length > 0) {
      const matchingGroup = partyGroups.find(g => g.name === group);
      if (matchingGroup) {
        filtersList.push({
          field: 'party_group_id',
          op: 'eq',
          value: matchingGroup.id.toString()
        });
      }
    }

    if (filtersList.length > 0) {
      params.filters = JSON.stringify(filtersList);
    }

    return params;
  }, [page, pageSize, sorting, columnFilters, group, partyGroups]);

  // Query to fetch parties
  const {
    data: partiesResponse = { items: [], total: 0 },
    isLoading: isPartiesLoading,
    isError: isPartiesError,
    error: partiesError,
    refetch: refetchParties,
  } = useQuery<PaginatedResponse<Party>>({
    queryKey: ['parties', userOrgId, queryParams],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      return listParties(queryParams, userOrgId);
    },
    enabled: !!userOrgId,
  });
  
  // Calculate page count from response
  const pageCount = useMemo(() => {
    if (partiesResponse) {
      return Math.ceil(partiesResponse.total / pageSize);
    }
    return 0;
  }, [partiesResponse, pageSize]);

  // Setup event listeners for edit/delete/client-user actions from the table
  useEffect(() => {
    const handleEditParty = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setIsEditFormOpen(true);
    };

    const handleDeleteParty = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setIsDeleteDialogOpen(true);
    };

    const handleCreateClientUser = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setClientUserMode('create');
      setIsClientUserDialogOpen(true);
    };

    const handleResetClientPin = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setClientUserMode('reset');
      setIsClientUserDialogOpen(true);
    };

    document.addEventListener('edit-party', handleEditParty as EventListener);
    document.addEventListener('delete-party', handleDeleteParty as EventListener);
    document.addEventListener('create-client-user', handleCreateClientUser as EventListener);
    document.addEventListener('reset-client-pin', handleResetClientPin as EventListener);

    return () => {
      document.removeEventListener('edit-party', handleEditParty as EventListener);
      document.removeEventListener('delete-party', handleDeleteParty as EventListener);
      document.removeEventListener('create-client-user', handleCreateClientUser as EventListener);
      document.removeEventListener('reset-client-pin', handleResetClientPin as EventListener);
    };
  }, []);
  
  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: PartyCreate) => createParty(data, userOrgId),
    onSuccess: () => {
      toast.success('Client created successfully');
      queryClient.invalidateQueries({ queryKey: ['parties', userOrgId] });
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PartyUpdate }) => updateParty(id, data),
    onSuccess: () => {
      toast.success('Client updated successfully');
      queryClient.invalidateQueries({ queryKey: ['parties', userOrgId] });
      setIsEditFormOpen(false);
      setCurrentParty(null);
    },
    onError: (error) => {
      toast.error(`Failed to update client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteParty(id),
    onSuccess: () => {
      toast.success('Client deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['parties', userOrgId] });
      setIsDeleteDialogOpen(false);
      setCurrentParty(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });
  
  // Event Handlers
  const handleCreateClick = () => {
    setIsCreateFormOpen(true);
  };

  const handleCreateSubmit = (formData: PartyCreate) => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (formData: PartyUpdate) => {
    if (currentParty) {
      updateMutation.mutate({ id: currentParty.id, data: formData });
    }
  };

  const handleDeleteConfirm = () => {
    if (currentParty) {
      deleteMutation.mutate(currentParty.id);
    }
  };
  
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
    setPage(newPagination.pageIndex);
  };
  
  // Handle filter changes
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    // Build the new filter states for nuqs
    const updates: Record<string, string | null> = {};

    // List of all possible filter keys
    const knownFilterKeys = ['name', 'phone', 'email', 'gstin', 'gst_type', 'party_group_id', 'site_id', 'state', 'source'];

    // First, reset all known keys to null
    knownFilterKeys.forEach(key => {
      updates[key] = null;
    });

    // Then add the active filters
    filters.forEach(filter => {
      const value = filter.value;
      updates[filter.id] = value !== undefined && value !== null && value !== '' ? String(value) : null;
    });

    // Update filters using nuqs
    setFilterStates(updates);

    // Reset to first page when filters change
    setPage(0);
  }, [setFilterStates, setPage]);
  
  // Contextual page config based on group query param
  const pageConfig = useMemo(() => {
    switch (group) {
      case 'Employee':
        return { title: 'Employees', desc: 'Manage employee records and contact information.', addLabel: 'Add Employee', entityName: 'employee' }
      case 'Consultants':
        return { title: 'Consultants', desc: 'Manage consultant records and contact information.', addLabel: 'Add Consultant', entityName: 'consultant' }
      case 'Client':
      default:
        return { title: 'Clients', desc: 'Manage client records and contact information.', addLabel: 'Add Client', entityName: 'client' }
    }
  }, [group])

  // Resolve group name to group ID for auto-assigning when creating
  const defaultGroupId = useMemo(() => {
    if (!group || !partyGroups.length) return null
    const match = partyGroups.find(g => g.name === group)
    return match?.id ?? null
  }, [group, partyGroups])

  // Create columns with dynamic party group and site options
  // Hide party_group_id column when a group is pre-selected via URL
  const columns = React.useMemo(() => {
    return baseColumns
      .filter(col => !(group && 'accessorKey' in col && col.accessorKey === 'party_group_id'))
      .map(col => {
        if ('accessorKey' in col && col.accessorKey === 'party_group_id' && col.meta?.variant === 'select') {
          return {
            ...col,
            meta: {
              ...col.meta,
              options: partyGroups.map(g => ({
                label: g.name,
                value: g.id.toString(),
              })),
            },
          };
        }
        if ('accessorKey' in col && col.accessorKey === 'site_id' && col.meta?.variant === 'select') {
          return {
            ...col,
            meta: {
              ...col.meta,
              options: sites.map(site => ({
                label: site.name,
                value: site.id.toString(),
              })),
            },
          };
        }
        return col;
      });
  }, [partyGroups, sites, group]);
  
  // --- Render Logic ---
  
  // Show loading indicator if auth state is loading
  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }
  
  // No organization assigned
  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-warning" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          You need to be assigned to an organization to manage clients.
          Please contact your administrator.
        </p>
        <div className="flex gap-4 mt-4">
          <Link to="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
          <Button onClick={logoutAction} variant="destructive">
            Logout
          </Button>
        </div>
      </div>
    );
  }
  
  // Handle error state
  if (isPartiesError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Clients</p>
        <p className="text-sm text-muted-foreground">
          {partiesError instanceof Error ? partiesError.message : 'An unknown error occurred.'}
        </p>
        <Button onClick={() => refetchParties()} variant="outline">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">{pageConfig.title}</h1>
        <Button onClick={handleCreateClick}>
          <Plus className="mr-2 h-4 w-4" /> {pageConfig.addLabel}
        </Button>
      </div>
      <p className="text-muted-foreground">
        {pageConfig.desc}
      </p>

      {/* Party Groups Management - only show when not filtered by a specific group */}
      {!group && (
        <PartyGroupManagement
          organizationId={userOrgId}
          onGroupsChange={() => queryClient.invalidateQueries({ queryKey: ['partyGroups', userOrgId] })}
        />
      )}
      
      {/* Parties Data Table */}
      <PartyDataTable
        data={partiesResponse}
        columns={columns}
        isLoading={isPartiesLoading || isGroupsLoading || isSitesLoading}
        partyGroups={partyGroups}
        pagination={{...pagination, pageCount}}
        sorting={sorting}
        columnFilters={columnFilters}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={handleFilterChange}
      />
      
      {/* Create Form */}
      <PartyForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
        partyGroups={partyGroups}
        sites={sites}
        defaultSiteId={defaultSiteId}
        defaultGroupId={defaultGroupId}
      />
      
      {/* Edit Form */}
      {currentParty && (
        <PartyForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentParty(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentParty}
          isLoading={updateMutation.isPending}
          mode="edit"
          partyGroups={partyGroups}
          sites={sites}
          defaultSiteId={defaultSiteId}
        />
      )}
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the client &quot;{currentParty?.name}&quot;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Client User Dialog */}
      <ClientUserDialog
        open={isClientUserDialogOpen}
        onOpenChange={setIsClientUserDialogOpen}
        party={currentParty}
        mode={clientUserMode}
      />
    </div>
  );
};

export default PartyPage; 